import { useEffect, type ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { multiFactor } from 'firebase/auth';
import { roleRequiresMfa, useSession } from '../lib/useSession';
import type { Role } from '../lib/sessionContext';
import { membershipFor } from '../lib/home';

/**
 * Guardia de UX: redirige a login o al enrolamiento TOTP.
 * La autorización real la aplica qr-api (R1.2); esto solo evita pantallas vacías.
 */
export function RequireAuth({ children, roles }: { children: ReactNode; roles?: Role[] }) {
  const { status, user, activeOrg, memberships, setActiveOrg } = useSession();
  const location = useLocation();
  const compatible = roles && activeOrg && !roles.includes(activeOrg.role) ? membershipFor(memberships, roles) : undefined;

  useEffect(() => {
    if (compatible) setActiveOrg(compatible.orgId);
  }, [compatible, setActiveOrg]);

  if (status === 'loading') {
    return <div className="p-6 text-sm text-ink-muted" role="status">Cargando…</div>;
  }
  if (status === 'signed-out' || !user) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }
  const hasSecondFactor = multiFactor(user).enrolledFactors.length > 0;
  if (roleRequiresMfa(activeOrg?.role) && !hasSecondFactor) {
    return <Navigate to="/login/segundo-factor" replace />;
  }
  if (roles && activeOrg && !roles.includes(activeOrg.role)) {
    // Otra organización del usuario tiene un rol válido para esta área: se activa. Si no, `/` elige su área.
    if (compatible) return <div className="p-6 text-sm text-ink-muted" role="status">Cargando…</div>;
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
}
